import { workManagementService } from '@/utils/workManagementService'
import { csIssueRepository } from '@/views/CSIssueReport/cs-issue-repository'
import { shouldUseDummyNotificationSource } from '@/policies/dummy-feature-visibility-policy.mjs'

import {
  createNotificationReadAllHandlers,
  normalizeCsNotification,
  normalizeSecurityNotification,
  normalizeSystemNotification,
  normalizeWorkNotification,
  paginateLocalNotifications,
} from './notification-center-domain.mjs'
import {
  getAllSecurityAlertNotifications,
  getSecurityAlertNotifications,
  markAllSecurityAlertsRead,
  markSecurityAlertRead,
} from './security-alert-api'
import { systemNotificationRepository } from './system-notification-repository'
import { workItemFlowLocation } from '@/views/WorkManagement/work-flow-detail-policy.mjs'

function pageResult(items, meta = {}, fallback = {}) {
  const total = Number(meta.total ?? meta.count ?? items.length)
  const pageSize = Number(meta.limit ?? meta.pageSize ?? fallback.pageSize ?? 30)
  const page = Number(meta.page ?? fallback.page ?? 1)
  return {
    items,
    total,
    page,
    maxPage: Number(meta.maxPage ?? meta.max_page ?? Math.max(1, Math.ceil(total / pageSize))),
    pageSize,
  }
}

const readAllHandlers = createNotificationReadAllHandlers({
  markAllSecurityNotificationsRead: () => markAllSecurityAlertsRead(),
  async getUnreadWorkNotifications() {
    const result = await workManagementService.getAllWorkNotifications({ unread: true })
    return result.data.map(normalizeWorkNotification)
  },
  markWorkNotificationRead: item => workManagementService.markNotificationRead(item.id),
  getSystemNotifications: () => systemNotificationRepository.list().map(normalizeSystemNotification),
  markSystemNotificationRead: item => systemNotificationRepository.markRead(item.id),
})

const sourceAdapters = {
  security: {
    async loadPage(options) {
      const data = await getSecurityAlertNotifications(options)
      return pageResult(data.results.map(normalizeSecurityNotification), { count: data.count }, options)
    },
    async getSummary() {
      const items = (await getAllSecurityAlertNotifications()).map(normalizeSecurityNotification)
      return { items, unreadCount: items.filter(item => !item.isRead).length }
    },
    markRead(item) {
      return markSecurityAlertRead(item.id)
    },
    markAllRead() {
      return readAllHandlers.security()
    },
  },
  work: {
    async loadPage(options) {
      const result = await workManagementService.getWorkNotificationsWithMeta({
        page: options.page,
        limit: options.pageSize,
      })
      return pageResult(result.data.map(normalizeWorkNotification), result.meta, options)
    },
    async getSummary() {
      const [result, unreadCount] = await Promise.all([
        workManagementService.getWorkNotificationsWithMeta({ page: 1, limit: 5, unread: true }),
        workManagementService.getNotificationUnreadCount(),
      ])
      return { items: result.data.map(normalizeWorkNotification), unreadCount }
    },
    markRead(item) {
      return workManagementService.markNotificationRead(item.id)
    },
    markAllRead() {
      return readAllHandlers.work()
    },
  },
  cs: {
    async loadPage(options) {
      const result = await csIssueRepository.loadNotifications(options)
      return { ...result, items: result.items.map(normalizeCsNotification), pageSize: result.limit }
    },
    async getSummary() {
      const result = await csIssueRepository.notificationSummary()
      return { ...result, items: result.items.map(normalizeCsNotification) }
    },
    markRead(item) { return csIssueRepository.markNotificationRead(item.id) },
    markAllRead() { return csIssueRepository.markAllNotificationsRead() },
  },
  system: {
    loadPage(options) {
      return Promise.resolve(paginateLocalNotifications(
        systemNotificationRepository.list().map(normalizeSystemNotification),
        options,
      ))
    },
    getSummary() {
      const items = systemNotificationRepository.list().map(normalizeSystemNotification)
      return Promise.resolve({ items, unreadCount: items.filter(item => !item.isRead).length })
    },
    markRead(item) {
      return Promise.resolve(systemNotificationRepository.markRead(item.id))
    },
    markAllRead() {
      return readAllHandlers.system()
    },
  },
}

export async function loadNotificationPage(source, options, context = {}) {
  const adapter = sourceAdapters[source]
  if (!adapter) throw new Error(`Unsupported notification source: ${source}`)
  if (!shouldUseDummyNotificationSource(source, process.env.NODE_ENV)) {
    return pageResult([], {}, options)
  }
  return adapter.loadPage(options, context)
}

export async function loadNotificationSummary(source, context = {}) {
  const adapter = sourceAdapters[source]
  if (!adapter) throw new Error(`Unsupported notification source: ${source}`)
  if (!shouldUseDummyNotificationSource(source, process.env.NODE_ENV)) {
    return { items: [], unreadCount: 0 }
  }
  return adapter.getSummary(context)
}

export async function markNotificationRead(item) {
  const adapter = sourceAdapters[item.source]
  if (!adapter) throw new Error(`Unsupported notification source: ${item.source}`)
  return adapter.markRead(item)
}

export async function markAllNotificationsRead(source, context = {}) {
  const adapter = sourceAdapters[source]
  if (!adapter?.markAllRead) throw new Error(`Unsupported notification source for mark all read: ${source}`)
  return adapter.markAllRead(context)
}

export async function resolveNotificationTarget(item) {
  if (!item?.target) return null
  if (item.target.kind === 'route') return item.target.location
  if (item.target.kind !== 'work') return null

  let projectId = null
  let workId = item.target.workId
  let runtimeNavigation = item.target.runtimeNavigation || item.raw?.runtime_navigation || null
  if (item.target.workItemId && (!workId || !runtimeNavigation)) {
    const workItem = await workManagementService.getWorkItem(item.target.workItemId)
    workId = workId || workItem?.work_id || workItem?.work?.id
    projectId = workItem?.project_id || workItem?.work?.project_id || null
    runtimeNavigation = runtimeNavigation || workItem?.runtime_navigation || null
  }
  if (workId && !projectId) {
    const context = await workManagementService.getWorkRouteContext(workId)
    projectId = context.projectId
  }
  if (!projectId || (!workId && !item.target.workItemId)) {
    const error = new Error('알림의 상세 화면을 찾을 수 없습니다.')
    error.code = 'NOTIFICATION_TARGET_NOT_FOUND'
    throw error
  }
  if (item.target.workItemId) {
    const location = workItemFlowLocation({
      projectId,
      workId,
      workItemId: item.target.workItemId,
      runtimeNavigation,
    })
    if (location) return location
    const error = new Error('할당된 Branch 이동 정보를 확인할 수 없습니다.')
    error.code = 'WORK_BRANCH_NAVIGATION_UNAVAILABLE'
    throw error
  }
  return { name: 'WorkDetail', params: { pk: projectId, work_id: workId } }
}

export function resetNotificationSources(permission) {
  csIssueRepository.resetForUser(permission)
  systemNotificationRepository.reset()
}
